import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import PartnersStrip from "@/components/PartnersStrip";
import Footer from "@/components/layout/Footer";
import { MessageCircle, Heart, Pin, Plus, ChevronRight, Lock, Paperclip, FileText, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const canPost = (u) => !!u && (u.is_admin || u.is_founder || u.is_founding_member || ["member", "collective", "paying", "pro"].includes(u.membership_tier));

const Community = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [discussions, setDiscussions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newDiscussion, setNewDiscussion] = useState({
    title: "",
    content: "",
    category: "general",
  });
  const [creating, setCreating] = useState(false);
  const [postAsBrand, setPostAsBrand] = useState(false);
  const [attachment, setAttachment] = useState(null); // {url, type, name}
  const [uploading, setUploading] = useState(false);

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    axios.get(`${API}/discussions/categories/all`)
      .then((res) => setCategories(res.data))
      .catch(() => {});

    if (user) {
      setLoading(true);
      const params = selectedCategory ? `?category=${selectedCategory}` : "";
      axios.get(`${API}/discussions${params}`)
        .then((res) => setDiscussions(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [selectedCategory, user]);

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to create a discussion");
      return;
    }
    if (!canPost(user)) {
      toast.error("Only paying members, founding members, and admins can post.");
      return;
    }

    if (!newDiscussion.title || !newDiscussion.content) {
      toast.error("Please fill in all fields");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...newDiscussion,
        post_as_brand: isAdmin && postAsBrand,
        attachment_url: attachment?.url || null,
        attachment_type: attachment?.type || null,
        attachment_name: attachment?.name || null,
      };
      const response = await axios.post(`${API}/discussions`, payload);
      setDiscussions([response.data, ...discussions]);
      setNewDiscussion({ title: "", content: "", category: "general" });
      setAttachment(null);
      setPostAsBrand(false);
      setIsCreateOpen(false);
      toast.success("Discussion created!");
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create discussion";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be 10MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload/discussion-attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachment(res.data);
      toast.success("Attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const MS_PER_MINUTE = 60000;
  const MS_PER_HOUR = 3600000;
  const MS_PER_DAY = 86400000;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
    const diffHours = Math.floor(diffMs / MS_PER_HOUR);
    const diffDays = Math.floor(diffMs / MS_PER_DAY);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const categoryColors = {
    general: "bg-[#E5E5E5]",
    introductions: "bg-[#FFC2E8]",
    collaborations: "bg-[#5170FF] text-white",
    opportunities: "bg-[#FF2D87] text-white",
    feedback: "bg-[#FFC2E8]",
    resources: "bg-[#0A0A0A] text-white",
    events: "bg-[#5170FF] text-white",
    wins: "bg-[#FFC2E8]",
  };

  // Not logged in — show gate
  if (!user) {
    return (
      <div className="min-h-screen bg-white" data-testid="community-page">
        <Navbar />

        {/* Hero */}
        <section className="bg-[#0A0A0A] py-16 md:py-24 text-white">
          <div className="container-main">
            <p className="overline text-[#FF2D87] mb-4">Community Forum</p>
            <h1 className="heading-hero mb-6">
              Join the<br />
              <span className="text-[#FFC2E8]">conversation</span>
            </h1>
            <p className="text-lg text-white/70 max-w-2xl">
              Connect with members, share wins, ask questions, and find collaborators.
            </p>
          </div>
        </section>

        {/* Login Gate */}
        <section className="section-padding">
          <div className="container-main">
            <div className="max-w-xl mx-auto text-center">
              <div className="bg-[#FFC2E8] border-2 border-[#0A0A0A] shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-12" data-testid="community-login-gate">
                <Lock className="w-16 h-16 text-[#FF2D87] mx-auto mb-6" />
                <h2 className="text-3xl font-display font-bold mb-4">Members Only</h2>
                <p className="text-[#0A0A0A] mb-8 max-w-md mx-auto">
                  The community forum is where the magic happens — introductions, collaborations, opportunities, wins, and real conversations. Sign up for free to join.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/login" className="btn-primary" data-testid="community-join-btn">
                    Join The Ensemble
                  </Link>
                  <Link to="/login" className="btn-secondary">
                    Login
                  </Link>
                </div>
              </div>

              {/* Preview of categories */}
              <div className="mt-12">
                <p className="overline text-[#5170FF] mb-6">Discussion Categories</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {categories.map((cat) => (
                    <span key={cat} className={`tag capitalize ${categoryColors[cat] || "bg-[#E5E5E5]"}`}>
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <PartnersStrip />
        <Footer />
      </div>
    );
  }

  // Logged in — show full community
  return (
    <div className="min-h-screen bg-white" data-testid="community-page">
      <Navbar />
      <DashboardHeader />

      {/* Section heading — slim, contained */}
      <section className="pt-8 md:pt-10 pb-2">
        <div className="container-main">
          <p className="overline text-[#FF2D87] mb-2">Community Forum</p>
          <p className="text-sm md:text-base text-[#4A4A4A] max-w-2xl">
            Share wins, ask questions, find collaborators — the conversation lives here.
          </p>
        </div>
      </section>

      {/* Filters & Create */}
      <section className="py-6 border-b-2 border-[#E5E5E5]">
        <div className="container-main">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory("")}
                className={`tag ${!selectedCategory ? "tag-blue" : ""}`}
                data-testid="filter-all"
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`tag capitalize ${selectedCategory === cat ? "tag-blue" : ""}`}
                  data-testid={`filter-${cat}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {canPost(user) ? (
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <button className="btn-primary flex items-center gap-2" data-testid="create-discussion-btn">
                    <Plus className="w-4 h-4" />
                    New Discussion
                  </button>
                </DialogTrigger>
                <DialogContent className="dialog-content sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-display">Start a Discussion</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateDiscussion} className="space-y-5 mt-2">
                    {isAdmin && (
                      <div className="bg-[#FFC2E8] p-3 border-2 border-[#0A0A0A] flex items-center justify-between" data-testid="post-as-toggle">
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A0A0A]">Posting as</p>
                          <p className="text-sm font-bold text-[#0A0A0A]">{postAsBrand ? "The Creative Ensemble" : (user?.name || "You")}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setPostAsBrand(!postAsBrand)}
                          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-2 rounded-full transition-colors ${postAsBrand ? "bg-[#0A0A0A] text-white" : "bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"}`}
                          data-testid="toggle-post-as-brand"
                        >
                          {postAsBrand ? "Switch to me" : "Post as Brand"}
                        </button>
                      </div>
                    )}
                    <div>
                      <label className="form-label">Category</label>
                      <select
                        value={newDiscussion.category}
                        onChange={(e) => setNewDiscussion({ ...newDiscussion, category: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#5170FF]"
                        data-testid="discussion-category-select"
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat} className="capitalize">{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        value={newDiscussion.title}
                        onChange={(e) => setNewDiscussion({ ...newDiscussion, title: e.target.value })}
                        placeholder="What's on your mind?"
                        className="input-underline"
                        data-testid="discussion-title-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Content</label>
                      <textarea
                        value={newDiscussion.content}
                        onChange={(e) => setNewDiscussion({ ...newDiscussion, content: e.target.value })}
                        placeholder="Share more details..."
                        className="form-textarea"
                        rows={5}
                        data-testid="discussion-content-input"
                      />
                    </div>
                    {/* Attachment */}
                    <div>
                      <label className="form-label">Attachment (image or PDF, max 10MB)</label>
                      {!attachment ? (
                        <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-[#0A0A0A] cursor-pointer hover:bg-[#FFC2E8] transition-colors text-sm font-bold">
                          <Paperclip className="w-4 h-4" />
                          {uploading ? "Uploading…" : "Add file"}
                          <input type="file" accept="image/*,application/pdf" onChange={handleAttachmentUpload} className="hidden" disabled={uploading} data-testid="attachment-input" />
                        </label>
                      ) : (
                        <div className="flex items-center gap-3 px-4 py-3 bg-[#FFC2E8] border-2 border-[#0A0A0A]" data-testid="attachment-preview">
                          {attachment.type === "image" ? (
                            <img src={attachment.url} alt="" className="w-12 h-12 object-cover border border-[#0A0A0A]" />
                          ) : (
                            <FileText className="w-6 h-6 text-[#0A0A0A]" />
                          )}
                          <span className="text-sm font-bold flex-1 truncate">{attachment.name}</span>
                          <button type="button" onClick={() => setAttachment(null)} className="text-[#0A0A0A] hover:text-[#FF2D87]" data-testid="remove-attachment-btn">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <button type="submit" disabled={creating || uploading} className="btn-primary w-full" data-testid="submit-discussion-btn">
                      {creating ? "Posting…" : "Post Discussion"}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <div className="bg-[#FFC2E8] border-2 border-[#0A0A0A] px-5 py-3 flex items-center gap-3" data-testid="posting-locked-banner">
                <Lock className="w-4 h-4 text-[#0A0A0A]" />
                <p className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">Posting is for paying members</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Discussions List */}
      <section className="py-10 md:py-14">
        <div className="container-main">
          {loading ? (
            <div className="space-y-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="bg-white border-2 border-[#0A0A0A] shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-6">
                  <div className="skeleton h-6 w-3/4 mb-3" />
                  <div className="skeleton h-4 w-full mb-2" />
                  <div className="skeleton h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : discussions.length === 0 ? (
            <div className="text-center py-20">
              <MessageCircle className="w-16 h-16 text-[#E5E5E5] mx-auto mb-4" />
              <p className="text-xl text-[#4A4A4A]">No discussions yet</p>
              <p className="text-[#4A4A4A] mt-2">Be the first to start a conversation!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {discussions.map((discussion) => (
                <Link
                  key={discussion.discussion_id}
                  to={`/community/${discussion.discussion_id}`}
                  className="discussion-thread block hover:-translate-y-1 hover:-translate-x-1 hover:shadow-[12px_12px_0px_0px_rgba(10,10,10,1)] transition-all"
                  data-testid={`discussion-${discussion.discussion_id}`}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      {discussion.author_picture ? (
                        <img src={discussion.author_picture} alt={discussion.author_name}
                          className="w-12 h-12 rounded-full border-2 border-[#0A0A0A] object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-[#5170FF] border-2 border-[#0A0A0A] flex items-center justify-center text-white font-bold">
                          {discussion.author_name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {discussion.is_pinned && <Pin className="w-4 h-4 text-[#FF2D87]" />}
                        <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 ${categoryColors[discussion.category] || "bg-[#E5E5E5]"}`}>
                          {discussion.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-display font-bold mb-2 line-clamp-1">{discussion.title}</h3>
                      <p className="text-[#4A4A4A] line-clamp-2 mb-3">{discussion.content}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-[#4A4A4A]">
                        <span className="font-medium">{discussion.author_name}</span>
                        <span>{formatDate(discussion.created_at)}</span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {discussion.replies_count} replies
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          {discussion.likes_count}
                        </span>
                        {discussion.attachment_type === "pdf" && (
                          <span className="flex items-center gap-1 text-[#FF2D87]">
                            <FileText className="w-4 h-4" /> PDF
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Image thumbnail — only shown when an image attachment exists */}
                    {discussion.attachment_type === "image" && discussion.attachment_url && (
                      <div className="hidden sm:block flex-shrink-0">
                        <img src={discussion.attachment_url} alt="" className="w-20 h-20 object-cover border-2 border-[#0A0A0A]" loading="lazy" />
                      </div>
                    )}
                    <div className="hidden md:flex items-center">
                      <ChevronRight className="w-6 h-6 text-[#E5E5E5]" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <PartnersStrip />
      <Footer />
    </div>
  );
};

export default Community;
